import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface CustomerOverview {
  id: number;
  customer_id: string;
  company_name: string;
  contact_person: string;
  phone_number: string;
  city: string;
  discount_percentage: number;
  is_active: boolean;
  total_orders: number;
  pending_orders: number;
  total_spent: number;
  amount_due: number;
  last_order_date: string | null;
  created_at: string;
}

const emptyForm = {
  username: '',
  email: '',
  company_name: '',
  contact_person: '',
  phone_number: '',
  address: '',
  city: '',
  postal_code: '',
  discount_percentage: '0.00',
  total_credit_limit: '0.00',
  password: '',
};

export type CustomerForm = typeof emptyForm;

export function useCompanyCustomers() {
  const { isExpired } = useAuth();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState<CustomerOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState({
    showModal: false, editingId: null as number | null, showPassword: false,
  });
  const { showModal, editingId, showPassword } = modalState;
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ error: '', success: '' });
  const { error, success } = feedback;
  const [limits, setLimits] = useState({ customerLimit: 0, canAddCustomer: true });
  const { customerLimit, canAddCustomer } = limits;

  const fetchCustomers = async () => {
    try {
      const response = await companyAPI.getCustomersOverview();
      setCustomers(response.data);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLimits = async () => {
    try {
      const res = await companyAPI.getDashboardStats();
      setLimits({ customerLimit: res.data.customer_limit || 0, canAddCustomer: res.data.can_add_customer !== false });
    } catch {}
  };

  useEffect(() => { fetchCustomers(); fetchLimits(); }, []);

  const handleOpenCreate = () => {
    setModalState({ showModal: true, editingId: null, showPassword: false });
    setForm(emptyForm);
    setFeedback(prev => ({ ...prev, error: '' }));
  };

  const handleOpenEdit = async (id: number) => {
    try {
      const response = await companyAPI.getCustomer(id);
      const c = response.data;
      setForm({
        username: c.username || '',
        email: c.email || '',
        company_name: c.company_name || '',
        contact_person: c.contact_person || '',
        phone_number: c.phone_number || '',
        address: c.address || '',
        city: c.city || '',
        postal_code: c.postal_code || '',
        discount_percentage: String(c.discount_percentage || '0.00'),
        total_credit_limit: String(c.total_credit_limit || '0.00'),
        password: '',
      });
      setModalState({ showModal: true, editingId: id, showPassword: false });
      setFeedback(prev => ({ ...prev, error: '' }));
    } catch (err) {
      console.error('Failed to fetch customer details:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(prev => ({ ...prev, error: '' }));
    try {
      const data: any = { ...form };
      if (!data.password) delete data.password;

      if (editingId) {
        await companyAPI.updateCustomer(editingId, data);
        setFeedback(prev => ({ ...prev, success: 'Customer updated successfully' }));
      } else {
        if (!data.password) {
          setFeedback(prev => ({ ...prev, error: 'Password is required for new customers.' }));
          return;
        }
        await companyAPI.createCustomer(data);
        setFeedback(prev => ({ ...prev, success: 'Customer created successfully' }));
      }
      setModalState(prev => ({ ...prev, showModal: false }));
      fetchCustomers();
      fetchLimits();
      setTimeout(() => setFeedback(prev => ({ ...prev, success: '' })), 3000);
    } catch (err: any) {
      const detail = err.response?.data;
      if (err.response?.status === 403 && detail?.error) {
        setFeedback(prev => ({ ...prev, error: detail.error }));
        setLimits(prev => ({ ...prev, canAddCustomer: false }));
      } else if (detail && typeof detail === 'object') {
        const messages = Object.entries(detail).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`);
        setFeedback(prev => ({ ...prev, error: messages.join('; ') }));
      } else {
        setFeedback(prev => ({ ...prev, error: 'Failed to save customer' }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    try {
      await companyAPI.deleteCustomer(id);
      setFeedback(prev => ({ ...prev, success: 'Customer deleted' }));
      fetchCustomers();
      fetchLimits();
      setTimeout(() => setFeedback(prev => ({ ...prev, success: '' })), 3000);
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const closeModal = () => setModalState(prev => ({ ...prev, showModal: false }));
  const toggleShowPassword = () => setModalState(prev => ({ ...prev, showPassword: !prev.showPassword }));
  const clearSuccess = () => setFeedback(prev => ({ ...prev, success: '' }));

  const addDisabled = isExpired || !canAddCustomer;
  const addTitle = isExpired
    ? 'Read-only mode — upgrade to add customers'
    : !canAddCustomer
      ? `You've reached the ${customerLimit}-client limit. Upgrade your plan to add more.`
      : undefined;

  return {
    // Auth
    isExpired,
    navigate,

    // State
    customers,
    loading,
    showModal,
    editingId,
    showPassword,
    form,
    setForm,
    saving,
    error,
    success,
    customerLimit,
    canAddCustomer,

    // Derived
    addDisabled,
    addTitle,

    // Handlers
    handleOpenCreate,
    handleOpenEdit,
    handleSave,
    handleDelete,
    closeModal,
    toggleShowPassword,
    clearSuccess,
  };
}
